import { defineNuxtPlugin, useNuxtApp, useRoute } from 'nuxt/app';
import { useTranslationStore } from '~~/common/stores/translation';
import { useTokenModeStore } from '~~/common/stores/tokenMode';

function createPluginProvides () {
  const { $i18n, $pinia } = useNuxtApp();

  const translationStore = useTranslationStore();
  const tokenModeStore = useTokenModeStore($pinia);
  {
    const isInTokenModeQuery = ['true', '1', ''].includes(<string>useRoute().query?.isInTokenMode ?? 'false');
    tokenModeStore.setIsInTokenMode(isInTokenModeQuery);
  }
  const awt: (key?: string, values?: Record<string, unknown>) => string = createAwt.bind(
    null,
    translationStore,
    tokenModeStore,
    $i18n,
  );

  return {
    awt: awt,
  };
}

export type PostTranslationPluginProvides = ReturnType<typeof createPluginProvides>;

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});

function createAwt (
  translationStore: ReturnType<typeof useTranslationStore>,
  tokenModeStore: ReturnType<typeof useTokenModeStore>,
  $i18n: ReturnType<typeof useNuxtApp>['$i18n'],
  translationKey?: string,
  values?: Record<string, unknown>,
) {
  // NOTE: getting i18n.messages would result in deepcopy (perf problems)
  // also this the lib author also uses _Messges
  // let translation: unknown = translationKey ? (translationStore[$i18n.locale.value]?.[translationKey] ?? undefined) : undefined;

  let translation: unknown;
  const locale = $i18n.locale.value;
  const localeTranslations = (translationStore as unknown as Record<string, Record<string, unknown>>)[locale];
  if (tokenModeStore.isInTokenMode) {
    //eslint disabled all type error
    translation = (translationKey
      ? `${localeTranslations?.[translationKey] ?? undefined} - ${translationKey}`
      : undefined
    );
  } else {
    translation = translationKey ? localeTranslations?.[translationKey] : undefined;
  }

  if (typeof translation === 'object' && translation !== null) {
    translation = (translation as Record<string, unknown>).text;
  }

  if (translation == null) {
    translation = `UNREG: ${translationKey}`;
  } else if (translation === '') {
    translation = `FIXME: ${translationKey}`;
  }

  let pluralized = pluralize(
    (typeof translation === 'string'
      ? translation
      : ''
    ),
    values,
  );

  if (values) {
    Object.entries(values).forEach(([key, value]) => {
      let keyIdx: number | string = key;
      if (!isNaN(parseInt(key))) {
        keyIdx = parseInt(key) + 1;
      }
      pluralized = pluralized.replace('%' + (keyIdx) + '%', String(value));
      pluralized = pluralized.replace('{' + (keyIdx) + '}', String(value));
    });
  }
  return pluralized;
}

/*
 * Frontend implementation of InlineDefinedPluralizer
 */
function pluralize (text: string, placeholders: Record<string, unknown> = {}): string {
  return text.replace(
    /~#%(\w+)%([^~]+)~~/g,
    (
      _match: string,
      placeholderIndex: number | string,
      groups: string,
    ): string => {
      const placeholderIndexNumber = Number(placeholderIndex);
      const placeholderValue = placeholders[!isNaN(placeholderIndexNumber) ? (placeholderIndexNumber - 1) : placeholderIndex];

      const matches = groups.matchAll(/([{[\]](\d+|\*)(,?\d+|\*)*[}[\]])([^|{[\]]+)\|?/g);
      let defaultMatch: string | null = null;
      for (const match of matches) {
        defaultMatch = defaultMatch || (match[4] ?? null);
        if (match[1] && matchesInterval(match[1], placeholderValue)) {
          return match[4] || '';
        }
      }
      return String(defaultMatch);
    },
  );
}

function matchesInterval (interval: string, value: unknown) {
  if (interval.match(/^{.*/)) {
    const values = interval.replace(/[{}]/g, '').split(',');
    if (values.length === 1 && values[0] === '*') {
      return true;
    }
    return values.includes('' + value);
  }

  const matches = interval.match(/([[\]])([^[\]]+)([[\]])/);
  if (!matches || matches.length !== 4) {
    return false;
  }
  const ld = matches[1];
  const rd = matches[3];
  const valuesStr = matches[2];
  if (!valuesStr) {
    return false;
  }
  const values = valuesStr.split(',');
  if (values.length !== 2 || !values[0] || !values[1] || values[0] > values[1]) {
    return false;
  }

  let contains = true;

  if (values[0] !== '*') {
    const numValue = Number(value);
    const numBound = Number(values[0]);
    contains = (ld === '[') ? (numValue >= numBound) : (numValue > numBound);
  }

  if (values[1] !== '*' && contains) {
    const numValue = Number(value);
    const numBound = Number(values[1]);
    contains = (rd === ']') ? (numValue <= numBound) : (numValue < numBound);
  }

  return contains;
}
